import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogActions } from 'src/app/model/dictionary';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { CompanyDto } from 'src/app/data-transfer/entities/doc-entities/company-dto';
import { CeoDto } from 'src/app/data-transfer/entities/doc-entities/ceo-dto';
import { SigneeDto } from 'src/app/data-transfer/entities/doc-entities/signee-dto';
import { forkJoin, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { CountryDto } from 'src/app/data-transfer/entities/country-dto';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { PpwrPersonDialogComponent, PpwrPersonDialogData } from '../../dialogs/ppwr-person-dialog/ppwr-person-dialog.component';
import { PpwrProducerDialogComponent, PpwrProducerDialogData } from '../../dialogs/ppwr-producer-dialog/ppwr-producer-dialog.component';
import { SignatureCaptureDialogComponent, SignatureCaptureDialogData } from '../../dialogs/signature-capture-dialog/signature-capture-dialog.component';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { FriendRequestApiService } from 'src/app/data-transfer/services/friend-request-api-service';
import { DocProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';
import { PpwrApiService } from 'src/app/data-transfer/services/ppwr-api.service';
import { LicenseService } from 'src/app/services/licensing-service';

@Component({
  selector: 'app-organization-settings',
  templateUrl: './organization-settings.component.html',
  styleUrls: ['./organization-settings.component.scss'],
})
export class OrganizationSettingsComponent implements OnInit, OnDestroy {
  readonly maxEntriesPerRole = 1000;
  readonly noDocProfileId = '';

  organization: OrganizationDto = new OrganizationDto();
  manufacturers: CompanyDto[] = [];
  authorizedRepresentatives: CompanyDto[] = [];
  ceos: CeoDto[] = [];
  signees: SigneeDto[] = [];
  docProfiles: DocProfileDto[] = [];
  organizationCode = '';
  private originalStateSnapshot: string = '';

  manufacturerSearchControl = new FormControl<string>('', { nonNullable: true });
  representativeSearchControl = new FormControl<string>('', { nonNullable: true });
  ceoSearchControl = new FormControl<string>('', { nonNullable: true });
  signeeSearchControl = new FormControl<string>('', { nonNullable: true });

  organizationForm = new FormGroup({
    address: new FormControl<string | null>(null),
    postCode: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null),
    location: new FormControl<string | null>(null),
  });

  private deletedManufacturerIds: number[] = [];
  private deletedRepresentativeIds: number[] = [];
  private deletedCeoIds: number[] = [];
  private deletedSigneeIds: number[] = [];

  private routeDataSubscription?: Subscription;
  private organizationCodeSubscription?: Subscription;

  isSaving = false;
  allCountries: CountryDto[] = [];
  isPPWRAllowed = false;

  constructor(
    private dialog: MatDialog,
    private userApiService: UserApiService,
    private friendRequestApiService: FriendRequestApiService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService,
    private ppwrApiService: PpwrApiService,
    private licenseService: LicenseService
  ) {}

  ngOnInit(): void {
    this.routeDataSubscription = this.route.data.subscribe(data => {
      this.allCountries = data['allCountries'] ?? [];
      this.setOrganizationData(data['ownOrganization'] as OrganizationDto);
    });

    this.organizationCodeSubscription = this.friendRequestApiService.getInviteCode(true).subscribe({
      next: dto => (this.organizationCode = dto.inviteCode),
    });

    this.isPPWRAllowed = this.licenseService.isPPWRAllowed;

    if (this.isPPWRAllowed) {
      this.ppwrApiService.getAllDocProfiles().subscribe({
        next: docProfiles => (this.docProfiles = docProfiles),
        error: error => console.error('Error while loading DOC profiles:', error),
      });
    }
  }

  ngOnDestroy(): void {
    this.routeDataSubscription?.unsubscribe();
    this.organizationCodeSubscription?.unsubscribe();
  }

  openSignatureDialog(person: CeoDto): void {
    const dialogData = new SignatureCaptureDialogData(
      `${person.firstName} ${person.lastName}`,
      person.signatureImageId ?? null,
      false,
      this.translocoService.translate('createDoc.signatureCaptureDialog.saveAndUseDescription')
    );
    this.dialog
      .open(SignatureCaptureDialogComponent, this.getPpwrDialogConfig(dialogData, '680px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        person.signatureImageId = result.data.signatureImageId;
      });
  }

  openCeoDialog(): void {
    this.openPersonDialog(this.translocoService.translate('createDoc.signersDialog.managingDirectorLabel'), 'person', false, null, this.ceos, this.deletedCeoIds);
  }

  editCeo(index: number): void {
    this.openPersonDialog(this.translocoService.translate('dataManagement.organizationSettings.dialogs.editCeo'), 'person', false, index, this.ceos, this.deletedCeoIds);
  }

  deleteCeo(index: number): void {
    this.deletePerson(index, this.ceos, this.deletedCeoIds);
  }

  openSigneeDialog(): void {
    this.openPersonDialog(this.translocoService.translate('createDoc.signersDialog.otherSignerTitle'), 'edit', true, null, this.signees, this.deletedSigneeIds);
  }

  editSignee(index: number): void {
    this.openPersonDialog(this.translocoService.translate('dataManagement.organizationSettings.dialogs.editSignee'), 'edit', true, index, this.signees, this.deletedSigneeIds);
  }

  deleteSignee(index: number): void {
    this.deletePerson(index, this.signees, this.deletedSigneeIds);
  }

  openManufacturerDialog(): void {
    this.openProducerDialog(
      this.translocoService.translate('createDoc.createDocDialog.producerLabel'),
      'factory',
      null,
      this.manufacturers,
      this.deletedManufacturerIds,
      this.docProfiles
    );
  }

  editManufacturer(index: number): void {
    this.openProducerDialog(
      this.translocoService.translate('dataManagement.organizationSettings.dialogs.editManufacturer'),
      'factory',
      index,
      this.manufacturers,
      this.deletedManufacturerIds,
      this.docProfiles
    );
  }

  deleteManufacturer(index: number): void {
    this.deleteProducer(index, this.manufacturers, this.deletedManufacturerIds);
  }

  openAuthorizedRepresentativeDialog(): void {
    this.openProducerDialog(
      this.translocoService.translate('createDoc.createDocDialog.representativeLabel'),
      'assignment_ind',
      null,
      this.authorizedRepresentatives,
      this.deletedRepresentativeIds,
      [],
      false
    );
  }

  editAuthorizedRepresentative(index: number): void {
    this.openProducerDialog(
      this.translocoService.translate('dataManagement.organizationSettings.dialogs.editRepresentative'),
      'assignment_ind',
      index,
      this.authorizedRepresentatives,
      this.deletedRepresentativeIds,
      [],
      false
    );
  }

  deleteAuthorizedRepresentative(index: number): void {
    this.deleteProducer(index, this.authorizedRepresentatives, this.deletedRepresentativeIds);
  }

  get filteredManufacturers(): CompanyDto[] {
    return this.filterCompanies(this.manufacturers, this.manufacturerSearchControl.value);
  }

  get selfManufacturer(): CompanyDto | undefined {
    return this.manufacturers[0];
  }

  get selfDefaultDocProfileId(): string {
    return this.selfManufacturer?.defaultDocProfileId ?? this.noDocProfileId;
  }

  setSelfDefaultDocProfileId(value: string): void {
    const self = this.selfManufacturer;
    if (!self) return;
    self.defaultDocProfileId = value === this.noDocProfileId ? null : value;
  }

  get filteredAuthorizedRepresentatives(): CompanyDto[] {
    return this.filterCompanies(this.authorizedRepresentatives, this.representativeSearchControl.value);
  }

  get filteredCeos(): CeoDto[] {
    return this.filterPersons(this.ceos, this.ceoSearchControl.value);
  }

  get filteredSignees(): SigneeDto[] {
    return this.filterPersons(this.signees, this.signeeSearchControl.value);
  }

  private filterCompanies(list: CompanyDto[], search: string): CompanyDto[] {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(c => c.companyName?.toLowerCase().includes(term));
  }

  private filterPersons<T extends CeoDto>(list: T[], search: string): T[] {
    const term = search.trim().toLowerCase();
    if (!term) return list;
    return list.filter(p => `${p.firstName} ${p.lastName} ${p.email}`.toLowerCase().includes(term));
  }

  hasChanges(): boolean {
    return this.createStateSnapshot() !== this.originalStateSnapshot;
  }

  saveChanges(): void {
    if (!this.hasChanges() || this.isSaving) return;

    this.isSaving = true;

    const originalState = JSON.parse(this.originalStateSnapshot || '{}');
    const requests = [
      ...(JSON.stringify(this.organizationForm.getRawValue()) !== JSON.stringify(originalState.formValues)
        ? [this.userApiService.updateOrganizationInfos(this.toCompanyDto())]
        : []),
      ...this.buildChangedCompanyRequests(originalState),
      ...this.buildChangedPersonRequests(originalState),
      ...this.buildDeleteRequests(),
    ];

    if (requests.length === 0) {
      this.isSaving = false;
      this.updateOriginalStateSnapshot();
      return;
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.showSnackbar('dataManagement.organizationSettings.messages.saveSuccess', 'app-success', 3000);
        this.isSaving = false;
        this.resetDeletedIds();
        this.loadOrganization();
      },
      error: error => {
        console.error('Error while saving organization settings:', error);
        this.showSnackbar('dataManagement.organizationSettings.messages.saveError', 'app-error', 4000);
        this.isSaving = false;
      },
    });
  }

  private loadOrganization(): void {
    this.userApiService.getOwnOrganization().subscribe({
      next: result => this.setOrganizationData(result),
      error: error => console.error('Error while loading organization:', error),
    });
  }

  cancelChanges(): void {
    const originalState = JSON.parse(this.originalStateSnapshot || '{}');
    this.manufacturers = structuredClone(originalState.manufacturers ?? []);
    this.authorizedRepresentatives = structuredClone(originalState.authorizedRepresentatives ?? []);
    this.ceos = structuredClone(originalState.ceos ?? []);
    this.signees = structuredClone(originalState.signees ?? []);
    this.patchForm(originalState.formValues);
    this.resetDeletedIds();
  }

  private toCompanyDto(): CompanyDto {
    const value = this.organizationForm.getRawValue();
    return {
      companyProfileId: this.organization.id,
      companyName: this.organization.name,
      address: value.address ?? null,
      postCode: value.postCode ?? null,
      city: value.city ?? null,
      location: value.location ?? null,
    };
  }

  private openPersonDialog<T extends CeoDto>(title: string, icon: string, showFunktion: boolean, index: number | null, list: T[], deletedIds: number[]): void {
    const data = new PpwrPersonDialogData(title, icon, showFunktion, index !== null ? list[index] : undefined);
    this.dialog
      .open(PpwrPersonDialogComponent, this.getPpwrDialogConfig(data, '680px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        if (index !== null) {
          list[index] = { ...list[index], ...result.data };
        } else {
          list.push({ personProfileId: -1, ...result.data } as T);
        }
      });
  }

  private openProducerDialog(
    title: string,
    icon: string,
    index: number | null,
    list: CompanyDto[],
    deletedIds: number[],
    docProfiles: DocProfileDto[] = [],
    showDocProfileField = true
  ): void {
    const data = new PpwrProducerDialogData(title, icon, this.allCountries, index !== null ? list[index] : undefined, docProfiles, showDocProfileField);
    this.dialog
      .open(PpwrProducerDialogComponent, this.getPpwrDialogConfig(data, '720px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        if (index !== null) {
          list[index] = { ...list[index], ...result.data };
        } else {
          list.push({ companyProfileId: -1, ...result.data });
        }
      });
  }

  private deletePerson<T extends CeoDto>(index: number, list: T[], deletedIds: number[]): void {
    const item = list[index];
    if (!item) return;
    if (item.personProfileId > 0) deletedIds.push(item.personProfileId);
    list.splice(index, 1);
  }

  private deleteProducer(index: number, list: CompanyDto[], deletedIds: number[]): void {
    const item = list[index];
    if (!item || item === this.selfManufacturer) return;
    if (item.companyProfileId > 0) deletedIds.push(item.companyProfileId);
    list.splice(index, 1);
  }

  private buildChangedCompanyRequests(originalState: any) {
    const requests = [];
    const changedManufacturers = this.getChangedItems(this.manufacturers, originalState.manufacturers ?? [], 'companyProfileId');
    const changedRepresentatives = this.getChangedItems(this.authorizedRepresentatives, originalState.authorizedRepresentatives ?? [], 'companyProfileId');
    if (changedManufacturers.length > 0) requests.push(this.userApiService.createOrUpdateManufacturerProfiles(changedManufacturers));
    if (changedRepresentatives.length > 0) requests.push(this.userApiService.createOrUpdateRepresentativeProfiles(changedRepresentatives));
    return requests;
  }

  private buildChangedPersonRequests(originalState: any) {
    const requests = [];
    const changedCeos = this.getChangedItems(this.ceos, originalState.ceos ?? [], 'personProfileId');
    const changedSignees = this.getChangedItems(this.signees, originalState.signees ?? [], 'personProfileId');
    if (changedCeos.length > 0) requests.push(this.userApiService.createOrUpdateCeoProfiles(changedCeos));
    if (changedSignees.length > 0) requests.push(this.userApiService.createOrUpdateSigneeProfiles(changedSignees));
    return requests;
  }

  private buildDeleteRequests() {
    const requests = [];
    if (this.deletedManufacturerIds.length > 0) requests.push(this.userApiService.deleteManufacturerProfiles(this.deletedManufacturerIds));
    if (this.deletedRepresentativeIds.length > 0) requests.push(this.userApiService.deleteRepresentativeProfiles(this.deletedRepresentativeIds));
    if (this.deletedCeoIds.length > 0) requests.push(this.userApiService.deleteCeoProfiles(this.deletedCeoIds));
    if (this.deletedSigneeIds.length > 0) requests.push(this.userApiService.deleteSigneeProfiles(this.deletedSigneeIds));
    return requests;
  }

  private getChangedItems<T>(current: T[], original: T[], idKey: keyof T): T[] {
    return current.filter(item => {
      if ((item[idKey] as number) < 0) return true;
      const originalItem = original.find((o: T) => o[idKey] === item[idKey]);
      return !originalItem || JSON.stringify(item) !== JSON.stringify(originalItem);
    });
  }

  private setOrganizationData(result: OrganizationDto): void {
    this.organization = result;
    this.manufacturers = (result.manufacturerProfiles ?? []).sort((a, b) => a.companyProfileId - b.companyProfileId);
    this.authorizedRepresentatives = (result.authorizedRepresentativeProfiles ?? []).sort((a, b) => a.companyProfileId - b.companyProfileId);
    this.ceos = (result.ceoProfiles ?? []).sort((a, b) => a.personProfileId - b.personProfileId);
    this.signees = (result.signeeProfiles ?? []).sort((a, b) => a.personProfileId - b.personProfileId);
    this.patchForm(result);
    this.updateOriginalStateSnapshot();
    this.resetDeletedIds();
  }

  private patchForm(values: { address?: string | null; postCode?: string | null; city?: string | null; location?: string | null }): void {
    this.organizationForm.patchValue({
      address: values.address ?? null,
      postCode: values.postCode ?? null,
      city: values.city ?? null,
      location: values.location ?? null,
    });
  }

  private updateOriginalStateSnapshot(): void {
    this.originalStateSnapshot = this.createStateSnapshot();
  }

  private createStateSnapshot(): string {
    return JSON.stringify({
      formValues: this.organizationForm.getRawValue(),
      manufacturers: this.manufacturers,
      authorizedRepresentatives: this.authorizedRepresentatives,
      ceos: this.ceos,
      signees: this.signees,
    });
  }

  private showSnackbar(messageKey: string, panelClass: string, duration: number): void {
    this.snackBar.open(this.translocoService.translate(messageKey), this.translocoService.translate('common.buttons.close'), {
      duration,
      panelClass: ['app-snackbar', panelClass],
    });
  }

  private getPpwrDialogConfig(data: unknown, width: string): MatDialogConfig {
    const config = getDialogConfig(data, width);
    config.maxWidth = 'calc(100vw - 32px)';
    config.panelClass = 'ppwr-dialog-panel';
    config.restoreFocus = true;
    return config;
  }

  private resetDeletedIds(): void {
    this.deletedManufacturerIds = [];
    this.deletedRepresentativeIds = [];
    this.deletedCeoIds = [];
    this.deletedSigneeIds = [];
  }
}
